import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Mail, Send, FileText, Download, RefreshCw, CheckCircle, XCircle, Clock, Edit, Save, X, Trash2, FileSpreadsheet, AlignLeft, Info, Upload, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OfferRelease = ({ user }) => {
  const [candidates, setCandidates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Selection states
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  
  // Email dialog
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [customBody, setCustomBody] = useState('');
  
  // Document editing
  const [editingDoc, setEditingDoc] = useState(null);
  const [docContent, setDocContent] = useState(null);
  const [editedParagraphs, setEditedParagraphs] = useState({});
  const [editedTableCells, setEditedTableCells] = useState({});
  const [savingDoc, setSavingDoc] = useState(false);
  
  // Upload states
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadDocName, setUploadDocName] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [candidatesRes, docsRes] = await Promise.all([
        axios.get(`${API}/offer-release/candidates`, config),
        axios.get(`${API}/offer-release/documents`, config)
      ]);
      
      setCandidates(candidatesRes.data);
      setDocuments(docsRes.data);
      setSelectedDocIds(docsRes.data.map(d => d.id));
    } catch (error) {
      toast.error('Failed to load offer release data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleDocSelection = (docId) => {
    setSelectedDocIds(prev => 
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const toggleAllDocs = () => {
    if (selectedDocIds.length === documents.length) {
      setSelectedDocIds([]);
    } else {
      setSelectedDocIds(documents.map(d => d.id));
    }
  };

  const toggleCandidateSelection = (candidateId) => {
    setSelectedCandidateIds(prev => 
      prev.includes(candidateId) ? prev.filter(id => id !== candidateId) : [...prev, candidateId]
    );
  };

  const toggleAllCandidates = () => {
    const eligibleIds = candidates.filter(c => !c.offer_email_sent && c.email).map(c => c.id);
    if (selectedCandidateIds.length === eligibleIds.length) {
      setSelectedCandidateIds([]);
    } else {
      setSelectedCandidateIds(eligibleIds);
    }
  };

  const handleDownloadDocument = async (docId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/offer-release/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleEditDocument = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/offer-release/documents/${doc.id}/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditingDoc(doc);
      setDocContent(response.data);
      setEditedParagraphs({});
      setEditedTableCells({});
    } catch (error) {
      toast.error('Failed to load document content');
    }
  };

  const handleParagraphChange = (index, text) => {
    setEditedParagraphs(prev => ({
      ...prev,
      [index]: text
    }));
  };

  const handleTableCellChange = (tableIdx, rowIdx, cellIdx, text) => {
    const key = `${tableIdx}-${rowIdx}-${cellIdx}`;
    setEditedTableCells(prev => ({
      ...prev,
      [key]: { tableIdx, rowIdx, cellIdx, text }
    }));
  };

  const handleSaveDocument = async () => {
    if (!editingDoc || !docContent) return;
    
    setSavingDoc(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare paragraphs to update
      const paragraphUpdates = Object.entries(editedParagraphs).map(([index, text]) => ({
        paragraph_index: parseInt(index),
        text: text
      }));
      
      // Prepare table cells to update
      const tableCellUpdates = Object.values(editedTableCells).map(cell => ({
        table_index: cell.tableIdx,
        row_index: cell.rowIdx,
        cell_index: cell.cellIdx,
        text: cell.text
      }));
      
      if (paragraphUpdates.length === 0 && tableCellUpdates.length === 0) {
        toast.info('No changes to save');
        setSavingDoc(false);
        return;
      }
      
      await axios.put(`${API}/offer-release/documents/${editingDoc.id}/content`, {
        paragraphs: paragraphUpdates.length > 0 ? paragraphUpdates : null,
        table_cells: tableCellUpdates.length > 0 ? tableCellUpdates : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Document saved successfully! Headers, footers, and images preserved.');
      setEditingDoc(null);
      setDocContent(null);
      setEditedParagraphs({});
      setEditedTableCells({});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save document');
    } finally {
      setSavingDoc(false);
    }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.docx')) {
      toast.error('Only Word documents (.docx) are allowed');
      return;
    }
    
    if (!uploadDocName.trim()) {
      toast.error('Please enter a document name');
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_name', uploadDocName);
      
      await axios.post(`${API}/offer-release/documents/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Document uploaded successfully');
      setShowUploadDialog(false);
      setUploadDocName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId, isCustom) => {
    const confirmMsg = isCustom 
      ? 'Are you sure you want to delete this custom document?' 
      : 'Are you sure you want to delete this base document? This action cannot be undone.';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/offer-release/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document deleted successfully');
      // Remove from selected if it was selected
      setSelectedDocIds(prev => prev.filter(id => id !== docId));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handlePrepareEmail = () => {
    if (selectedCandidateIds.length === 0) {
      toast.error('Please select at least one candidate');
      return;
    }
    if (selectedDocIds.length === 0) {
      toast.error('Please select at least one document to attach');
      return;
    }
    
    const deadline = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
    
    const emailBody = `Dear {candidate_name},

Congratulations on being selected to join the Grey Bull Technologies family.

As the next step toward completing your onboarding process, please fill out, sign, and submit the attached documents. Kindly scan the completed documents and share them with us by 2:00 PM on ${deadline}. Please note that failure to submit the documents within the specified timeline may result in the withdrawal of the offer.

Instructions for Filling the Documents:
• Please read all instructions and documents carefully before filling them out.
• Ensure that all documents are fully completed.
• All fields are mandatory. If a field does not apply to you, please write "NOT APPLICABLE" instead of leaving it blank.
• All information must be accurate and filled in CAPITAL LETTERS only.

Kindly share the signed and scanned copies of the completed documents, along with mandatory documents as mentioned in the offer letter.

For any queries or assistance, please contact:

Neha Sharma - 8527197919

We wish you all the best and look forward to welcoming you to Grey Bull Technologies LLP.


Thanks & Regards,
HR`;

    setCustomBody(emailBody);
    setShowEmailDialog(true);
  };

  const handleSendBulkEmail = async () => {
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/offer-release/send-bulk-email`, {
        candidate_ids: selectedCandidateIds,
        document_ids: selectedDocIds,
        custom_body: customBody
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const results = response.data.results;
      if (results.success.length > 0) {
        toast.success(`Offer email sent to ${results.success.length} candidate(s)`);
      }
      if (results.failed.length > 0) {
        toast.error(`Failed for ${results.failed.length} candidate(s)`);
      }
      if (results.skipped.length > 0) {
        toast.info(`Skipped ${results.skipped.length} candidate(s) (already sent)`);
      }
      
      setShowEmailDialog(false);
      setSelectedCandidateIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleUpdateStatus = async (candidateId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/offer-release/candidates/${candidateId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status updated to ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteCandidate = async (candidateId) => {
    if (!window.confirm('Are you sure you want to delete this candidate? This action cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/recruitment/candidates/${candidateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Candidate deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete candidate');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { 
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Accepted':
        return <Badge className="bg-green-500 hover:bg-green-600"><CheckCircle className="w-3 h-3 mr-1" />Accepted</Badge>;
      case 'Declined':
        return <Badge className="bg-red-500 hover:bg-red-600"><XCircle className="w-3 h-3 mr-1" />Declined</Badge>;
      case 'Sent':
        return <Badge className="bg-blue-500 hover:bg-blue-600"><Mail className="w-3 h-3 mr-1" />Sent</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const getStatusRowClass = (status) => {
    switch (status) {
      case 'Accepted': return 'bg-green-50 hover:bg-green-100';
      case 'Declined': return 'bg-red-50 hover:bg-red-100';
      default: return 'hover:bg-gray-50';
    }
  };

  const eligibleCandidates = candidates.filter(c => !c.offer_email_sent && c.email);
  const selectedDocs = documents.filter(d => selectedDocIds.includes(d.id));
  const selectedCandidates = candidates.filter(c => selectedCandidateIds.includes(c.id));
  // Check if user can edit/upload documents (Admin, SuperAdmin, or HR department)
  const canEdit = user?.role === 'admin' || 
                  user?.role === 'superadmin' || 
                  user?.department?.toLowerCase() === 'hr' || 
                  user?.department?.toLowerCase() === 'human resources';
  const canDelete = user?.role === 'admin' || user?.role === 'superadmin';

  return (
    <div className="space-y-6" data-testid="offer-release-tab">
      {/* Documents Section */}
      <Card data-testid="offer-documents-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              <div>
                <CardTitle className="text-lg">Offer Documents</CardTitle>
                <CardDescription>Select documents to attach, download or edit (preserves headers, footers & watermarks)</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {canEdit && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowUploadDialog(true)}
                  data-testid="upload-doc-btn"
                  className="flex items-center gap-1"
                >
                  <Upload className="w-4 h-4" />
                  Upload Document
                </Button>
              )}
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="select-all-docs"
                  checked={selectedDocIds.length === documents.length && documents.length > 0}
                  onCheckedChange={toggleAllDocs}
                />
                <Label htmlFor="select-all-docs" className="text-sm">Select All</Label>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {documents.map((doc) => (
              <Card 
                key={doc.id} 
                className={`border-2 transition-colors ${selectedDocIds.includes(doc.id) ? 'border-indigo-400 bg-indigo-50' : 'border-dashed hover:border-indigo-300'}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col items-center text-center gap-3">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id={`doc-${doc.id}`}
                        checked={selectedDocIds.includes(doc.id)}
                        onCheckedChange={() => toggleDocSelection(doc.id)}
                        data-testid={`checkbox-doc-${doc.id}`}
                      />
                      <FileText className="w-8 h-8 text-indigo-500" />
                    </div>
                    <div className="min-h-[40px] flex flex-col justify-center">
                      <p className="font-medium text-sm break-words text-center leading-tight">{doc.name}</p>
                      <p className="text-xs text-gray-500 break-all text-center">{doc.filename}</p>
                      {doc.is_custom && (
                        <Badge variant="outline" className="text-xs mt-1 bg-purple-50 text-purple-700 mx-auto">Custom</Badge>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap justify-center">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                        data-testid={`download-doc-${doc.id}`}
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {canEdit && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleEditDocument(doc)}
                          data-testid={`edit-doc-${doc.id}`}
                          title="Edit document content"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      {canEdit && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleDeleteDocument(doc.id, doc.is_custom)}
                          data-testid={`delete-doc-${doc.id}`}
                          title="Delete document"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-4 text-sm text-gray-600">
            {selectedDocIds.length} of {documents.length} documents selected
          </div>
        </CardContent>
      </Card>

      {/* Candidates Table */}
      <Card data-testid="offer-candidates-card">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-indigo-600" />
            <div>
              <CardTitle className="text-lg">Selected Candidates for Offer Release</CardTitle>
              <CardDescription>Select candidates and documents, then click "Send Email"</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              onClick={handlePrepareEmail}
              disabled={selectedCandidateIds.length === 0 || selectedDocIds.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="send-bulk-email-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Email ({selectedCandidateIds.length})
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No candidates eligible for offer release.</p>
              <p className="text-sm">Candidates must be "Selected" with a future Date of Joining.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="offer-candidates-table">
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={selectedCandidateIds.length === eligibleCandidates.length && eligibleCandidates.length > 0}
                        onCheckedChange={toggleAllCandidates}
                        data-testid="select-all-candidates"
                      />
                    </TableHead>
                    <TableHead className="font-bold">Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Aadhar</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>DOJ</TableHead>
                    <TableHead>Offer Release</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {candidates.map((candidate) => (
                    <TableRow key={candidate.id} className={getStatusRowClass(candidate.offer_status)} data-testid={`offer-row-${candidate.id}`}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedCandidateIds.includes(candidate.id)}
                          onCheckedChange={() => toggleCandidateSelection(candidate.id)}
                          disabled={candidate.offer_email_sent || !candidate.email}
                          data-testid={`checkbox-candidate-${candidate.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{candidate.name}</TableCell>
                      <TableCell>{candidate.email || <span className="text-red-500 text-sm">No email</span>}</TableCell>
                      <TableCell className="text-sm">{candidate.aadhar_number || '-'}</TableCell>
                      <TableCell>{candidate.role}</TableCell>
                      <TableCell>{formatDate(candidate.date_of_joining)?.split(',')[0]}</TableCell>
                      <TableCell>{candidate.offer_release_date ? formatDate(candidate.offer_release_date) : '-'}</TableCell>
                      <TableCell>
                        <Select value={candidate.offer_status || 'Pending'} onValueChange={(value) => handleUpdateStatus(candidate.id, value)}>
                          <SelectTrigger className="w-32 h-8" data-testid={`status-select-${candidate.id}`}>
                            <SelectValue>{getStatusBadge(candidate.offer_status)}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="Sent">Sent</SelectItem>
                            <SelectItem value="Accepted">Accepted</SelectItem>
                            <SelectItem value="Declined">Declined</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {candidate.offer_email_sent && <Badge variant="secondary" className="text-xs">Sent</Badge>}
                          {canDelete && (
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteCandidate(candidate.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-offer-candidate-${candidate.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="mt-4 text-sm text-gray-500">
            {selectedCandidateIds.length} selected | {eligibleCandidates.length} eligible
          </div>
        </CardContent>
      </Card>

      {/* Bulk Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Send Offer Email to {selectedCandidateIds.length} Candidate(s)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Sender Info */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <Label className="text-sm font-medium text-blue-800">From (Your Email)</Label>
              <p className="text-sm text-blue-900 font-medium">{user?.name || 'HR Team'} &lt;{user?.email}&gt;</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <Label className="text-sm font-medium">Recipients ({selectedCandidates.length})</Label>
              <div className="flex flex-wrap gap-2">
                {selectedCandidates.map(c => (
                  <Badge key={c.id} variant="outline">{c.name} ({c.email})</Badge>
                ))}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <Label className="text-sm font-medium">Subject</Label>
              <p className="text-sm">Congratulations! Offer Confirmation & Joining Formalities</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Attachments ({selectedDocs.length})</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedDocs.map((doc) => (
                  <Badge key={doc.id} variant="outline" className="flex items-center gap-1">
                    <FileText className="w-3 h-3" />{doc.filename}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Email Body</Label>
              <Textarea value={customBody} onChange={(e) => setCustomBody(e.target.value)} rows={12} className="mt-2 font-mono text-sm" data-testid="email-body-input" />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
              <Button onClick={handleSendBulkEmail} disabled={sendingEmail} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-send-bulk-email">
                {sendingEmail ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Edit Dialog */}
      <Dialog open={!!editingDoc} onOpenChange={(open) => !open && setEditingDoc(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="edit-doc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit: {editingDoc?.name}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              <Info className="w-4 h-4" />
              Headers, footers, images, and watermarks are preserved. Edit text content below.
            </DialogDescription>
          </DialogHeader>
          
          {docContent && (
            <Tabs defaultValue={
              docContent.paragraphs?.filter(p => p.text.trim()).length === 0 && docContent.tables?.length > 0 
                ? "tables" 
                : "paragraphs"
            } className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="paragraphs" className="flex items-center gap-2">
                  <AlignLeft className="w-4 h-4" />
                  Paragraphs ({docContent.paragraphs?.filter(p => p.text.trim()).length || 0})
                </TabsTrigger>
                <TabsTrigger value="tables" className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4" />
                  Tables ({docContent.tables?.length || 0})
                </TabsTrigger>
              </TabsList>
              
              {/* Paragraphs Tab */}
              <TabsContent value="paragraphs" className="space-y-3 max-h-[50vh] overflow-y-auto p-2">
                {docContent.paragraphs?.filter(p => p.text.trim()).map((para) => (
                  <div key={para.index} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <Label className="text-xs text-gray-500">Paragraph {para.index + 1} ({para.style})</Label>
                      {editedParagraphs[para.index] !== undefined && (
                        <Badge variant="outline" className="text-xs bg-yellow-100">Modified</Badge>
                      )}
                    </div>
                    <Textarea
                      value={editedParagraphs[para.index] !== undefined ? editedParagraphs[para.index] : para.text}
                      onChange={(e) => handleParagraphChange(para.index, e.target.value)}
                      rows={2}
                      className="text-sm"
                      data-testid={`para-input-${para.index}`}
                    />
                  </div>
                ))}
                {(!docContent.paragraphs || docContent.paragraphs.filter(p => p.text.trim()).length === 0) && (
                  <p className="text-center text-gray-500 py-4">
                    No editable paragraphs in this document.
                    {docContent.tables?.length > 0 && (
                      <span className="block mt-2 text-blue-600">
                        This document has {docContent.tables.length} table(s). Click the "Tables" tab to edit table content.
                      </span>
                    )}
                  </p>
                )}
              </TabsContent>
              
              {/* Tables Tab */}
              <TabsContent value="tables" className="space-y-4 max-h-[50vh] overflow-y-auto p-2">
                {docContent.tables?.map((table, tableIdx) => (
                  <Card key={tableIdx} className="border-2">
                    <CardHeader className="py-2 px-4">
                      <CardTitle className="text-sm">Table {tableIdx + 1} ({table.rows.length} rows)</CardTitle>
                    </CardHeader>
                    <CardContent className="p-2 overflow-x-auto">
                      <Table className="text-sm">
                        <TableBody>
                          {table.rows.map((row, rowIdx) => (
                            <TableRow key={rowIdx}>
                              {row.cells.map((cell, cellIdx) => {
                                const key = `${tableIdx}-${rowIdx}-${cellIdx}`;
                                const isEdited = editedTableCells[key] !== undefined;
                                return (
                                  <TableCell key={cellIdx} className="p-1">
                                    <div className="relative">
                                      <Input
                                        value={isEdited ? editedTableCells[key].text : cell.text}
                                        onChange={(e) => handleTableCellChange(tableIdx, rowIdx, cellIdx, e.target.value)}
                                        className={`text-xs h-8 ${isEdited ? 'border-yellow-400 bg-yellow-50' : ''}`}
                                        data-testid={`table-cell-${tableIdx}-${rowIdx}-${cellIdx}`}
                                      />
                                    </div>
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ))}
                {(!docContent.tables || docContent.tables.length === 0) && (
                  <p className="text-center text-gray-500 py-4">No tables in this document</p>
                )}
              </TabsContent>
            </Tabs>
          )}

          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-gray-500">
              {Object.keys(editedParagraphs).length + Object.keys(editedTableCells).length} change(s) pending
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setEditingDoc(null)}>
                <X className="w-4 h-4 mr-2" />Cancel
              </Button>
              <Button onClick={handleSaveDocument} disabled={savingDoc} className="bg-green-600 hover:bg-green-700" data-testid="save-doc-btn">
                {savingDoc ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Changes</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-md" data-testid="upload-doc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload New Document
            </DialogTitle>
            <DialogDescription>
              Upload a Word document (.docx) to add to the offer documents. Font will be set to Aptos, size 8.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="doc-name">Document Name *</Label>
              <Input 
                id="doc-name"
                placeholder="e.g., NDA Agreement"
                value={uploadDocName}
                onChange={(e) => setUploadDocName(e.target.value)}
                data-testid="upload-doc-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="doc-file">Select File (.docx) *</Label>
              <Input 
                id="doc-file"
                type="file"
                accept=".docx"
                ref={fileInputRef}
                onChange={handleUploadDocument}
                disabled={uploading || !uploadDocName.trim()}
                data-testid="upload-doc-file-input"
                className="cursor-pointer"
              />
            </div>
            
            {uploading && (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Uploading and processing document...
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => {
              setShowUploadDialog(false);
              setUploadDocName('');
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfferRelease;
